(function () {
  'use strict';

  var viewport = null;
  var active = null;
  var queue = [];
  var nextId = 0;
  var timer = null;
  var leaveTimer = null;
  var remaining = 0;
  var startedAt = 0;
  var leaving = false;
  var obstructionTrackingReady = false;
  var obstructionUpdatePending = false;
  var observedStickies = [];
  var stickyResizeObserver = null;
  var stickyMutationObserver = null;
  var durations = { success: 4000, info: 6000, warning: 8000, error: 0 };
  var icons = {
    success: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"/></svg>',
    info: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/></svg>',
    warning: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 2.8 20h18.4L12 3Z"/><path d="M12 9v5M12 17.5v.5"/></svg>',
    error: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v6M12 17v.5"/></svg>'
  };

  function normalize(options) {
    options = typeof options === 'string' ? { title: options } : (options || {});
    var variant = durations[options.variant] ? options.variant : 'success';
    if (options.variant === 'error') variant = 'error';
    var requestedDuration = options.duration == null ? NaN : Number(options.duration);
    var duration = Number.isFinite(requestedDuration) && requestedDuration >= 0
      ? requestedDuration
      : (options.action && variant !== 'error' ? 10000 : durations[variant]);
    return {
      id: options.id || 'bb-toast-' + (++nextId),
      variant: variant,
      title: String(options.title || ''),
      message: String(options.message || ''),
      action: options.action && options.action.label && options.action.href ? options.action : null,
      duration: duration,
      dedupeKey: options.dedupeKey ? String(options.dedupeKey) : ''
    };
  }

  function ensureViewport() {
    if (!viewport) viewport = document.querySelector('[data-bb-toast-viewport]');
    if (viewport) setupObstructionTracking();
    return viewport;
  }

  function measureObstruction() {
    if (!viewport) return;
    var obstruction = 0;
    document.querySelectorAll('[data-bb-product-sticky].visible:not([hidden])').forEach(function (sticky) {
      var rect = sticky.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
      obstruction = Math.max(obstruction, window.innerHeight - rect.top);
    });
    viewport.style.setProperty('--bb-toast-obstruction', Math.max(0, Math.ceil(obstruction)) + 'px');
  }

  function scheduleObstructionUpdate() {
    if (obstructionUpdatePending) return;
    obstructionUpdatePending = true;
    window.requestAnimationFrame(function () {
      obstructionUpdatePending = false;
      measureObstruction();
    });
  }

  function observeStickies() {
    document.querySelectorAll('[data-bb-product-sticky]').forEach(function (sticky) {
      if (observedStickies.indexOf(sticky) > -1) return;
      observedStickies.push(sticky);
      if (stickyResizeObserver) stickyResizeObserver.observe(sticky);
      if (stickyMutationObserver) {
        stickyMutationObserver.observe(sticky, {
          attributes: true,
          attributeFilter: ['class', 'hidden']
        });
      }
    });
    scheduleObstructionUpdate();
  }

  function setupObstructionTracking() {
    if (obstructionTrackingReady) return;
    obstructionTrackingReady = true;
    if (typeof window.ResizeObserver === 'function') {
      stickyResizeObserver = new window.ResizeObserver(scheduleObstructionUpdate);
    }
    if (typeof window.MutationObserver === 'function') {
      stickyMutationObserver = new window.MutationObserver(scheduleObstructionUpdate);
    }
    observeStickies();
    window.addEventListener('resize', scheduleObstructionUpdate, { passive: true });
    document.addEventListener('shopify:section:load', observeStickies);
    document.addEventListener('transitionend', function (event) {
      if (event.target && event.target.closest && event.target.closest('[data-bb-product-sticky]')) {
        scheduleObstructionUpdate();
      }
    });
  }

  function clearTimer() {
    if (timer) window.clearTimeout(timer);
    timer = null;
  }

  function clearLeaveTimer() {
    if (leaveTimer) window.clearTimeout(leaveTimer);
    leaveTimer = null;
  }

  function startTimer() {
    clearTimer();
    if (!active || remaining <= 0) return;
    startedAt = Date.now();
    timer = window.setTimeout(function () { dismiss(active.id); }, remaining);
  }

  function pauseTimer() {
    if (!timer) return;
    remaining = Math.max(0, remaining - (Date.now() - startedAt));
    clearTimer();
  }

  function resumeTimer() {
    if (active && !leaving) startTimer();
  }

  function makeIcon(variant) {
    var icon = document.createElement('span');
    icon.className = 'bb-toast-item__icon bb-toast-item__icon--' + variant;
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = icons[variant];
    return icon;
  }

  function render(item) {
    if (!ensureViewport()) return;
    clearLeaveTimer();
    leaving = false;
    viewport.textContent = '';
    var toast = document.createElement('div');
    toast.className = 'bb-toast-item bb-toast-item--' + item.variant;
    toast.dataset.toastId = item.id;
    toast.setAttribute('role', item.variant === 'error' ? 'alert' : 'status');
    toast.setAttribute('aria-live', item.variant === 'error' ? 'assertive' : 'polite');
    toast.setAttribute('aria-atomic', 'true');

    var content = document.createElement('div');
    content.className = 'bb-toast-item__content';
    var title = document.createElement('strong');
    title.className = 'bb-toast-item__title';
    title.textContent = item.title;
    content.appendChild(title);
    if (item.message) {
      var message = document.createElement('span');
      message.className = 'bb-toast-item__message';
      message.textContent = item.message;
      content.appendChild(message);
    }
    if (item.action) {
      var action = document.createElement('a');
      action.className = 'bb-toast-item__action';
      action.href = item.action.href;
      action.textContent = item.action.label;
      if (item.action.openInNewTab) {
        action.target = '_blank';
        action.rel = 'noopener';
        var newWindow = document.createElement('span');
        newWindow.className = 'visually-hidden';
        newWindow.textContent = ' ' + (viewport.dataset.newWindowLabel || 'Opens in a new window');
        action.appendChild(newWindow);
      }
      content.appendChild(action);
    }

    var close = document.createElement('button');
    close.className = 'bb-toast-item__close';
    close.type = 'button';
    close.setAttribute('aria-label', viewport.dataset.dismissLabel || 'Dismiss notification');
    close.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
    close.addEventListener('click', function () { dismiss(item.id); });

    toast.appendChild(makeIcon(item.variant));
    toast.appendChild(content);
    toast.appendChild(close);
    toast.addEventListener('mouseenter', pauseTimer);
    toast.addEventListener('mouseleave', resumeTimer);
    toast.addEventListener('focusin', pauseTimer);
    toast.addEventListener('focusout', function (event) {
      if (!toast.contains(event.relatedTarget)) resumeTimer();
    });
    viewport.appendChild(toast);
    window.requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    remaining = item.duration;
    startTimer();
    scheduleObstructionUpdate();
  }

  function showNext() {
    if (active || !queue.length) return;
    active = queue.shift();
    render(active);
  }

  function show(options) {
    var item = normalize(options);
    if (item.variant === 'error' && active && active.variant !== 'error') {
      clearTimer();
      clearLeaveTimer();
      active = item;
      render(active);
      return item.id;
    }
    if (item.dedupeKey && active && active.dedupeKey === item.dedupeKey) {
      active = item;
      render(active);
      return item.id;
    }
    if (item.dedupeKey) {
      var queuedIndex = queue.findIndex(function (queued) { return queued.dedupeKey === item.dedupeKey; });
      if (queuedIndex > -1) {
        queue[queuedIndex] = item;
        return item.id;
      }
    }
    if (queue.length >= 5) queue.shift();
    queue.push(item);
    showNext();
    return item.id;
  }

  function dismiss(id) {
    if (id && (!active || active.id !== id)) {
      queue = queue.filter(function (item) { return item.id !== id; });
      return;
    }
    if (!active || leaving) return;
    leaving = true;
    clearTimer();
    var toast = ensureViewport() && viewport.querySelector('.bb-toast-item');
    if (toast) toast.classList.add('is-leaving');
    leaveTimer = window.setTimeout(function () {
      leaveTimer = null;
      if (viewport) viewport.textContent = '';
      active = null;
      leaving = false;
      showNext();
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180);
  }

  window.BBToast = { show: show, dismiss: dismiss };
  document.addEventListener('DOMContentLoaded', ensureViewport, { once: true });
})();
